import functionRegistry from './functionRegistry';

// Global registries for tracking initialized instances
declare global {
    interface Window {
        agentRegistry: Record<string, any>;
        prebidRegistry: Record<string, any>;
        pbjs: any;
    }
}

// Initialize global registries
export const initializeRegistries = () => {
    if (typeof window !== 'undefined') {
        window.agentRegistry = window.agentRegistry || {};
        window.prebidRegistry = window.prebidRegistry || {};
        window.pbjs = window.pbjs || { que: [] };
    }
};

// Agent version map from AdvancedAgent
export const iiqAgents = {
    none: '',
    5.413: 'https://agent.intentiq.com/Agent/5.413/Unminified/IIQUniversalID.js',
    5.4131: 'https://agent.intentiq.com/Agent/5.4131/Unminified/IIQUniversalID.js',
    5.4132: 'https://agent.intentiq.com/Agent/5.4132/Unminified/IIQUniversalID.js',
    5.4133: 'https://agent.intentiq.com/Agent/5.4133/UniversalID.js', // obfuscated
    '6.0': 'https://agent.intentiq.com/Agent/6.0/Unminified/IIQAgent.js',
    6.01: 'https://agent.intentiq.com/Agent/6.01/Unminified/IIQAgent.js',
    6.02: 'https://agent.intentiq.com/Agent/6.02/Unminified/IIQAgent.js',
    6.03: 'https://agent.intentiq.com/Agent/6.03/Unminified/IIQAgent.js',
    6.031: 'https://agent.intentiq.com/Agent/6.031/%20Unminified/IIQAgent.js',
    6.032: 'https://agent.intentiq.com/Agent/6.032/Unminified/IIQAgent.js',
    6.0331: 'https://agent.intentiq.com/Agent/6.0331/es6/Unminified/IIQAgent.js',
    6.034: 'https://agent.intentiq.com/Agent/6.034/es6/Unminified/IIQAgent.js',
    6.04: 'https://agent.intentiq.com/Agent/6.04/es6/Unminified/IIQAgent.js',
    6.041: 'https://agent.intentiq.com/Agent/6.041/es6/Unminified/IIQAgent.js',
    6.05: 'https://agent.intentiq.com/Agent/6.05/es6/Unminified/IIQAgent.js',
    6.051: 'https://agent.intentiq.com/Agent/6.051/es6/Unminified/IIQAgent.js',
    6.06: 'https://agent.intentiq.com/Agent/6.06/es6/Unminified/IIQAgent.js',
    6.061: 'https://agent.intentiq.com/Agent/6.061/es6/Unminified/IIQAgent.js',
    6.07: 'https://agent.intentiq.com/Agent/6.07/es6/Unminified/IIQAgent.js',
    6.071: 'https://agent.intentiq.com/Agent/6.071/es6/Unminified/IIQAgent.js',
    6.072: 'https://agent.intentiq.com/Agent/6.072/UniversalID/IIQAgent.js', // obfuscated
    6.073: 'https://agent.intentiq.com/Agent/6.073/UniversalID/IIQAgent.js', // obfuscated
    6.08: 'https://agent.intentiq.com/Agent/6.08/es6/Unminified/IIQAgent.js',
    6.09: 'https://agent.intentiq.com/Agent/6.09/es6/Unminified/IIQAgent.js',
    6.091: 'https://agent.intentiq.com/Agent/6.091/es6/Unminified/IIQAgent.js',
    6.1: 'https://agent.intentiq.com/Agent/6.1/es6/Unminified/IIQAgent.js',
    6.11: 'https://agent.intentiq.com/Agent/6.11/es6/Unminified/IIQAgent.js',
    6.111: 'https://agent.intentiq.com/Agent/6.111/es6/Unminified/IIQAgent.js',
    6.112: 'https://agent.intentiq.com/Agent/6.112/es6/Unminified/IIQAgent.js',
    6.12: 'https://agent.intentiq.com/Agent/6.12/es6/Unminified/IIQAgent.js',
    6.121: 'https://agent.intentiq.com/Agent/6.121/es6/Unminified/IIQAgent.js',
    6.122: 'https://agent.intentiq.com/Agent/6.122/es6/Unminified/IIQAgent.js',
    rawdata: 'https://iiq-prebid-test.s3.us-east-1.amazonaws.com/IntentIqRawDataObject.js',
    ecommerce: 'https://iiq-prebid-test.s3.us-east-1.amazonaws.com/IntentIqECommerceObject.js'
};

// Prebid version map from AdvancedAgent
export const pbjsVersions = {
    7.49: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-7.49.0.js',
    8.17: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-8.17.0.js',
    8.44: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-8.44.0.js',
    9.0: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.0.0.js',
    9.27: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.27.0.js',
    9.3: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.30.0.js',
    9.32: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.32.0.js',
    9.33: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.33.0.js',
    9.42: 'https://iiq-prebid-test.s3.amazonaws.com/prebid-9.42.0.js'
};

// Utility function to create script tags
export const createScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (!src) {
            reject(new Error('Script source is required'));
            return;
        }

        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
};

// Individual function implementations
export const downloadAgentScript = async (version: string): Promise<void> => {
    const scriptUrl = iiqAgents[version as keyof typeof iiqAgents];
    if (!scriptUrl) {
        throw new Error(`Invalid agent version: ${version}`);
    }
    await createScript(scriptUrl);
};

export const initializeAgent = async (version: string, name: string, async: boolean): Promise<void> => {
    // First ensure the script is loaded
    await downloadAgentScript(version);

    // Check if the agent constructor is available
    const AgentConstructor = (window as any).IntentIqObject || (window as any).IntentIqSyncObject;
    if (!AgentConstructor) {
        throw new Error('Agent constructor not found. Make sure the agent script is loaded.');
    }

    // Create agent configuration
    const config = {
        partner: 12345, // Default partner ID
        pbjs: window.pbjs,
        callback: (eids: any, group: any, agentSource: any) => {
            console.log('Agent callback triggered:', { eids, group, agentSource });
        }
    };

    // Initialize the agent
    const agentInstance = new AgentConstructor(config);
    
    // Store in registry
    window.agentRegistry[name] = {
        instance: agentInstance,
        version,
        config,
        timestamp: new Date()
    };
};

export const downloadPrebidScript = async (version: string): Promise<void> => {
    const scriptUrl = pbjsVersions[version as unknown as keyof typeof pbjsVersions];
    if (!scriptUrl) {
        throw new Error(`Invalid Prebid version: ${version}`);
    }
    await createScript(scriptUrl);
};

export const initializePrebid = async (name: string, config?: object): Promise<void> => {
    // Create a named pbjs instance
    const pbjsInstance: any = { que: [] };
    
    // Apply configuration if provided
    if (config) {
        pbjsInstance.setConfig = (cfg: any) => {
            console.log('Setting Prebid config:', cfg);
            // In a real implementation, this would apply the config
        };
        pbjsInstance.setConfig(config);
    }

    // Store in registry
    window.prebidRegistry[name] = {
        instance: pbjsInstance,
        config,
        timestamp: new Date()
    };
};

// Registry management functions
export const clearRegistries = () => {
    if (typeof window !== 'undefined') {
        window.agentRegistry = {};
        window.prebidRegistry = {};
    }
};

export const getRegistryStatus = () => {
    if (typeof window === 'undefined') {
        return { agentRegistry: {}, prebidRegistry: {} };
    }
    
    return {
        agentRegistry: window.agentRegistry || {},
        prebidRegistry: window.prebidRegistry || {}
    };
};

// Initialize the function registry (no built-in functions)
export const initializeFunctionRegistry = () => {
    // No built-in functions are registered by default
    // Users can add their own functions through the UI
    console.log('Function registry initialized - no built-in functions loaded');
};

// Legacy function execution handler (for backward compatibility)
export const executeFunction = async (functionName: string, params: any[]): Promise<any> => {
    return functionRegistry.executeFunction(functionName, params);
};

// Get available functions (for backward compatibility)
export const getAvailableFunctions = () => {
    return functionRegistry.getAllFunctions();
};

// Export the function registry for use in other modules
export { functionRegistry }; 